import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle, XCircle, Clock, User, Calendar, MessageSquare } from "lucide-react";

type Request = {
  id: number;
  salonId: number;
  appointmentId: number;
  requestedBy: number;
  requestedByName: string | null;
  reason: string | null;
  status: "pending" | "approved" | "rejected";
  reviewedBy: number | null;
  reviewedByName: string | null;
  reviewNote: string | null;
  createdAt: Date;
  reviewedAt: Date | null;
  appointment: {
    id: number;
    clientName: string | null;
    startTime: Date;
    status: string;
    professionalName: string | null;
  } | null;
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendente",
  approved: "Aprovado",
  rejected: "Rejeitado",
};
const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  approved: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

export default function AdminCancellationRequests() {
  const [filter, setFilter] = useState<"pending" | "approved" | "rejected" | undefined>("pending");
  const [reviewTarget, setReviewTarget] = useState<Request | null>(null);
  const [decision, setDecision] = useState<"approved" | "rejected" | null>(null);
  const [reviewNote, setReviewNote] = useState("");

  const { data: requests, isLoading, refetch } = (trpc as any).cancellationRequests.list.useQuery(
    { status: filter },
    { refetchOnWindowFocus: true, refetchInterval: 10000 }
  );

  const reviewMutation = (trpc as any).cancellationRequests.review.useMutation({
    onSuccess: () => {
      toast.success(decision === "approved" ? "Cancelamento aprovado!" : "Cancelamento rejeitado.");
      setReviewTarget(null);
      setDecision(null);
      setReviewNote("");
      refetch();
    },
    onError: (e: any) => toast.error(e.message ?? "Erro ao processar solicitação."),
  });

  function openReview(req: Request, d: "approved" | "rejected") {
    setReviewTarget(req);
    setDecision(d);
    setReviewNote("");
  }

  function formatDate(d: Date | string | null) {
    if (!d) return "—";
    return new Date(d).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Solicitações de Cancelamento</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Gerencie as solicitações de cancelamento enviadas pela equipe. Apenas administradores podem aprovar ou rejeitar.
        </p>
      </div>

      {/* Filtros */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { value: "pending", label: "Pendentes" },
          { value: "approved", label: "Aprovados" },
          { value: "rejected", label: "Rejeitados" },
          { value: undefined, label: "Todos" },
        ].map((opt) => (
          <Button
            key={String(opt.value)}
            size="sm"
            variant={filter === opt.value ? "default" : "outline"}
            onClick={() => setFilter(opt.value as any)}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Carregando...</div>
      ) : !requests || requests.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Nenhuma solicitação {filter === "pending" ? "pendente" : ""} encontrada.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req: Request) => (
            <div key={req.id} className="border rounded-xl p-4 bg-card shadow-sm">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  {/* Cabeçalho */}
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Badge className={`text-xs border ${STATUS_COLORS[req.status]}`}>
                      {STATUS_LABELS[req.status]}
                    </Badge>
                    <span className="text-xs text-muted-foreground">#{req.id}</span>
                  </div>

                  {/* Dados do agendamento */}
                  <div className="flex items-center gap-1.5 text-sm font-medium mb-1">
                    <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                    {req.appointment ? (
                      <span>
                        {req.appointment.clientName ?? "Cliente"} —{" "}
                        {formatDate(req.appointment.startTime)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Agendamento #{req.appointmentId}</span>
                    )}
                  </div>

                  {/* Solicitante */}
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-1">
                    <User className="w-4 h-4 shrink-0" />
                    <span>Solicitado por <strong>{req.requestedByName ?? "Equipe"}</strong> em {formatDate(req.createdAt)}</span>
                  </div>

                  {/* Motivo */}
                  {req.reason && (
                    <div className="flex items-start gap-1.5 text-sm text-muted-foreground mt-1">
                      <MessageSquare className="w-4 h-4 shrink-0 mt-0.5" />
                      <span className="italic">"{req.reason}"</span>
                    </div>
                  )}

                  {/* Nota do revisor */}
                  {req.reviewNote && (
                    <div className="mt-2 text-xs bg-muted/50 rounded px-2 py-1.5 text-muted-foreground">
                      <strong>Nota do admin:</strong> {req.reviewNote}
                      {req.reviewedByName && <span> — {req.reviewedByName}</span>}
                    </div>
                  )}
                </div>

                {/* Ações (apenas para pendentes) */}
                {req.status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-700 border-green-300 hover:bg-green-50"
                      onClick={() => openReview(req, "approved")}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" /> Aprovar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => openReview(req, "rejected")}
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Rejeitar
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog de confirmação */}
      <Dialog open={!!reviewTarget} onOpenChange={(o) => { if (!o) { setReviewTarget(null); setDecision(null); setReviewNote(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {decision === "approved" ? "✅ Aprovar cancelamento" : "❌ Rejeitar cancelamento"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {reviewTarget?.appointment && (
              <div className="text-sm bg-muted/50 rounded-lg p-3">
                <p><strong>Cliente:</strong> {reviewTarget.appointment.clientName ?? "—"}</p>
                <p><strong>Data:</strong> {formatDate(reviewTarget.appointment.startTime)}</p>
                <p><strong>Solicitado por:</strong> {reviewTarget.requestedByName ?? "—"}</p>
                {reviewTarget.reason && <p><strong>Motivo:</strong> {reviewTarget.reason}</p>}
              </div>
            )}
            <div>
              <label className="text-sm font-medium block mb-1">
                Nota para a equipe <span className="text-muted-foreground font-normal">(opcional)</span>
              </label>
              <Textarea
                placeholder={decision === "approved" ? "Ex: Aprovado conforme solicitado." : "Ex: Cancelamento não autorizado neste horário."}
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                rows={3}
              />
            </div>
            {decision === "approved" && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
                ⚠️ Ao aprovar, o agendamento será cancelado imediatamente e o cliente será notificado via WhatsApp (se disponível).
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReviewTarget(null); setDecision(null); setReviewNote(""); }}>
              Voltar
            </Button>
            <Button
              className={decision === "approved" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
              disabled={reviewMutation.isPending}
              onClick={() => {
                if (!reviewTarget || !decision) return;
                reviewMutation.mutate({ id: reviewTarget.id, decision, reviewNote: reviewNote || undefined });
              }}
            >
              {reviewMutation.isPending ? "Processando..." : decision === "approved" ? "Confirmar aprovação" : "Confirmar rejeição"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
